import React, { useState, useEffect, useRef } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { GlobalDropdownField, DropdownOption } from '../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import QRCode from 'qrcode';
import 'primeicons/primeicons.css';

interface Batch {
  batch_id: string;
  batch_name: string;
  batch_time: string;
  course_id: string;
  course_name?: string;
  is_active: boolean;
}

interface Student {
  student_id: string;
  enrollment_id: string;
  name: string;
  dateOfBirth: string | null;
  gender: string | null;
  mobileNumber: string | null;
  fatherName: string | null;
  motherName: string | null;
  district: string | null;
  state: string | null;
  villageTown: string | null;
  profilePicture: { cdn_url?: string } | null;
  status: string;
  date_of_admission: string;
  is_registered: boolean;
  category: string | null;
  bloodGroup: string | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  validTill?: string | null;
}

interface BatchInfo {
  batch_id: string;
  batch_name: string;
  batch_time: string;
  course_name: string | null;
  course_title: string | null;
  is_active: boolean;
  valid_till?: string | null;
}

interface CardProps {
  student: Student;
  batchInfo: BatchInfo | null;
  schoolName: string;
  schoolAddress: string;
  schoolPhone?: string;
  websiteUrl: string;
  principalSign?: string;
  validTill?: string | null;
}

const getInitials = (name: string) => {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatDOB = (dob: string | null) => {
  if (!dob) return '-';
  try { return new Date(dob).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return dob; }
};

const formatDate = (date: string | null | undefined) => {
  if (!date) return '-';
  try { return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return date; }
};

const HeaderPattern: React.FC<{ id: string }> = ({ id }) => (
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
    <defs>
      <pattern id={id} patternUnits="userSpaceOnUse" width="80" height="80">
        <path d="M15 15 L10 20 L15 25" stroke="rgba(251,178,31,0.15)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M25 15 L30 20 L25 25" stroke="rgba(251,178,31,0.15)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M58 50 Q55 50 55 53 L55 57 Q55 60 52 60 Q55 60 55 63 L55 67 Q55 70 58 70" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <path d="M62 50 Q65 50 65 53 L65 57 Q65 60 68 60 Q65 60 65 63 L65 67 Q65 70 62 70" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <circle cx="65" cy="25" r="1.5" fill="rgba(251,178,31,0.15)" />
        <circle cx="20" cy="40" r="1.5" fill="rgba(255,255,255,0.1)" />
        <rect x="50" y="15" width="3" height="3" fill="rgba(255,255,255,0.15)" rx="0.5" />
        <rect x="70" y="40" width="3" height="3" fill="rgba(251,178,31,0.2)" rx="0.5" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#${id})`} />
  </svg>
);

const CARD_W = 560;
const CARD_H = 320;

const cardShell: React.CSSProperties = {
  width: CARD_W,
  height: CARD_H,
  background: '#fff',
  borderRadius: 20,
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(14,34,93,0.18), 0 4px 16px rgba(14,34,93,0.10)',
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
};

const navy = '#0e225d';
const gold = '#fbb21f';

const IdCardFront: React.FC<CardProps> = ({ student, batchInfo, schoolName, schoolAddress, websiteUrl, validTill }) => {
  const resolvedValidTill = validTill || student.validTill || batchInfo?.valid_till || null;
  const patId = `fp-${student.student_id}`;

  return (
    <div style={cardShell}>
      {/* Left Strip */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 160,
        background: 'linear-gradient(180deg, #0e225d 0%, #1a3a7a 60%, #234495 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '20px 12px', zIndex: 2,
      }}>
        <HeaderPattern id={patId} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${navy}, ${gold})`, zIndex: 3 }} />

        <div style={{
          width: 44, height: 44, borderRadius: '50%', background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)', marginBottom: 8, position: 'relative', zIndex: 3,
        }}>
          <i className="pi pi-building" style={{ fontSize: 20, color: navy }}></i>
        </div>
        <div style={{ fontSize: 10, fontWeight: 800, color: '#fff', textAlign: 'center', lineHeight: 1.3, position: 'relative', zIndex: 3 }}>{schoolName}</div>
        <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.65)', textAlign: 'center', marginTop: 4, lineHeight: 1.4, position: 'relative', zIndex: 3 }}>{schoolAddress}</div>

        <div style={{ width: '80%', height: 1, background: 'rgba(255,255,255,0.15)', margin: '12px 0', position: 'relative', zIndex: 3 }} />

        <div style={{
          width: 80, height: 80, borderRadius: 40, border: '3px solid rgba(255,255,255,0.3)',
          overflow: 'hidden', position: 'relative', zIndex: 3, background: gold,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {student.profilePicture?.cdn_url
            ? <img src={student.profilePicture.cdn_url} alt={student.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 26, fontWeight: 800, color: navy }}>{getInitials(student.name)}</span>
          }
          {student.is_registered && (
            <div style={{ position: 'absolute', bottom: 3, right: 3, width: 16, height: 16, background: '#16a34a', borderRadius: '50%', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="pi pi-check" style={{ fontSize: 8, color: '#fff' }}></i>
            </div>
          )}
        </div>

        <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', textAlign: 'center', marginTop: 10, lineHeight: 1.3, position: 'relative', zIndex: 3 }}>{student.name}</div>

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6,
          background: 'rgba(251,178,31,0.2)', border: '1px solid rgba(251,178,31,0.4)',
          borderRadius: 6, padding: '3px 8px', position: 'relative', zIndex: 3,
        }}>
          <i className="pi pi-graduation-cap" style={{ fontSize: 9, color: gold }}></i>
          <span style={{ fontSize: 8, fontWeight: 700, color: gold, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Student</span>
        </div>

        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${navy}, ${gold})`, zIndex: 3 }} />
      </div>

      {/* Right Content */}
      <div style={{ marginLeft: 160, flex: 1, display: 'flex', flexDirection: 'column', padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 8, fontWeight: 700, color: gold, textTransform: 'uppercase', letterSpacing: '1px' }}>Student Identity Card</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: navy, letterSpacing: '-0.3px', marginTop: 2 }}>{student.name}</div>
          </div>
          <div style={{
            padding: '4px 10px', background: 'rgba(14,34,93,0.06)', border: '1px solid rgba(14,34,93,0.12)',
            borderRadius: 8, fontSize: 10, fontWeight: 700, color: navy,
          }}>
            {student.enrollment_id || student.student_id.slice(-6).toUpperCase()}
          </div>
        </div>

        {/* Info Grid — removed Course, Batch Timing, Batch, Mobile, Address */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', flex: 1 }}>
          {[
            { icon: 'pi-user', label: "Father's Name", value: student.fatherName },
            { icon: 'pi-heart', label: "Mother's Name", value: student.motherName },
            { icon: 'pi-calendar', label: 'Date of Birth', value: formatDOB(student.dateOfBirth) },
            { icon: 'pi-users', label: 'Gender', value: student.gender },
            { icon: 'pi-heart-fill', label: 'Blood Group', value: student.bloodGroup },
            { icon: 'pi-tag', label: 'Category', value: student.category },
            { icon: 'pi-calendar-plus', label: 'Admission Date', value: formatDate(student.date_of_admission) },
            { icon: 'pi-shield', label: 'Valid Till', value: formatDate(resolvedValidTill) },
          ].map((row, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <div style={{
                width: 24, height: 24, borderRadius: 6, background: '#E8EEF7',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1,
              }}>
                <i className={`pi ${row.icon}`} style={{ fontSize: 10, color: navy }}></i>
              </div>
              <div>
                <div style={{ fontSize: 7.5, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{row.label}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: navy, marginTop: 1 }}>{row.value || '-'}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ height: 3, background: `linear-gradient(90deg, ${navy}, ${gold}, ${navy})`, borderRadius: 2, marginTop: 10 }} />
      </div>
    </div>
  );
};

const IdCardBack: React.FC<CardProps & { qrUrl?: string }> = ({ student, batchInfo, schoolName, schoolAddress, websiteUrl, principalSign = 'ℰ𝓊𝓌', validTill }) => {
  const qrRef = useRef<HTMLCanvasElement>(null);
  const patId = `bp-${student.student_id}`;

  useEffect(() => {
    if (qrRef.current) {
      QRCode.toCanvas(qrRef.current, `https://${websiteUrl}`, {
        width: 72, margin: 1, color: { dark: navy, light: '#ffffff' },
      });
    }
  }, [websiteUrl]);

  const emergencyName = student.emergencyContactName || student.fatherName || student.motherName || null;
  const emergencyPhone = student.emergencyContactPhone || student.mobileNumber || null;
  const address = [student.villageTown, student.district, student.state].filter(Boolean).join(', ') || '-';

  return (
    <div style={cardShell}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0e225d 0%, #1a3a7a 60%, #234495 100%)',
        padding: '14px 20px', position: 'relative', overflow: 'hidden', flexShrink: 0,
      }}>
        <HeaderPattern id={patId} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${navy}, ${gold})`, zIndex: 3 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 3 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="pi pi-building" style={{ fontSize: 16, color: navy }}></i>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>{schoolName}</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{schoolAddress}</div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Student ID</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: gold }}>{student.enrollment_id || student.student_id.slice(-6).toUpperCase()}</div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', gap: 0, overflow: 'hidden' }}>
        {/* Left — Contact & Info */}
        <div style={{ flex: 1, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10, borderRight: '1px solid #E8EEF7' }}>
          <div>
            <div style={{ fontSize: 8, fontWeight: 700, color: navy, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="pi pi-phone" style={{ color: gold, fontSize: 9 }}></i> Contact Details
            </div>
            {[
              { label: 'Mobile', value: student.mobileNumber, icon: 'pi-phone' },
              { label: 'Address', value: address, icon: 'pi-map-marker' },
            ].map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ width: 24, height: 24, borderRadius: 6, background: '#E8EEF7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`pi ${r.icon}`} style={{ fontSize: 10, color: navy }}></i>
                </div>
                <div>
                  <div style={{ fontSize: 7.5, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase' }}>{r.label}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: navy }}>{r.value || '-'}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: 'rgba(14,34,93,0.04)', borderRadius: 10, padding: '8px 10px', border: '1px solid rgba(14,34,93,0.10)' }}>
            <div style={{ fontSize: 8, fontWeight: 700, color: navy, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="pi pi-exclamation-triangle" style={{ color: gold, fontSize: 9 }}></i> Emergency Contact
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 7.5, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Name</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: navy, marginTop: 1 }}>{emergencyName || '-'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 7.5, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Phone</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: navy, marginTop: 1 }}>{emergencyPhone || '-'}</div>
              </div>
            </div>
            {student.bloodGroup && (
              <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                <i className="pi pi-heart-fill" style={{ fontSize: 9, color: '#dc2626' }}></i>
                <span style={{ fontSize: 7.5, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Blood Group:</span>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#dc2626' }}>{student.bloodGroup}</span>
              </div>
            )}
          </div>

          <div style={{ background: 'rgba(251,178,31,0.06)', borderRadius: 8, padding: '6px 8px', border: '1px solid rgba(251,178,31,0.2)' }}>
            <div style={{ fontSize: 7.5, fontWeight: 700, color: navy, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Note</div>
            {[
              'This card is property of the institute.',
              'Must be carried within institute premises.',
              'Non-transferable and valid only for enrolled course.',
            ].map((note, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 4, marginBottom: 2 }}>
                <div style={{ width: 3, height: 3, borderRadius: '50%', background: gold, marginTop: 3.5, flexShrink: 0 }} />
                <div style={{ fontSize: 8, color: '#6b7280', lineHeight: 1.4 }}>{note}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — QR + Signature */}
        <div style={{ width: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', padding: '14px 12px', background: 'rgba(14,34,93,0.02)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 8, fontWeight: 700, color: navy, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Scan to Verify</div>
            <div style={{ width: 80, height: 80, border: '2px solid #E8EEF7', borderRadius: 10, overflow: 'hidden', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
              <canvas ref={qrRef} />
            </div>
            <div style={{ fontSize: 8, fontWeight: 700, color: navy, marginTop: 6, letterSpacing: '0.5px' }}>{websiteUrl}</div>
          </div>

          <div style={{ textAlign: 'center', width: '100%', borderTop: '1px solid #E8EEF7', paddingTop: 10 }}>
            <div style={{ fontSize: 28, color: '#1a3a7a', fontStyle: 'italic', letterSpacing: '-1px', fontWeight: 700, lineHeight: 1, marginBottom: 4 }}>{principalSign}</div>
            <div style={{ width: '70%', height: 1, background: navy, margin: '4px auto 4px' }} />
            <div style={{ fontSize: 8, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Issuing Authority</div>
          </div>
        </div>
      </div>

      <div style={{ height: 3, background: `linear-gradient(90deg, ${navy}, ${gold}, ${navy})`, flexShrink: 0 }} />
    </div>
  );
};

// ─── Print HTML builder ────────────────────────────────────────────────────────
// Front card on LEFT, Back panel on RIGHT (side by side per student)

const buildPrintHTML = (
  cards: Student[],
  batchInfo: BatchInfo | null,
  schoolName: string,
  schoolAddress: string,
  websiteUrl: string,
  validTill: string,
) => {
  const fmtDate = (d: string | null | undefined) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  const frontCard = (s: Student) => {
    const vt = fmtDate(validTill || s.validTill || batchInfo?.valid_till || null);
    const patternId = `pattern-front-${s.student_id}`;
    // Removed: Course, Batch Timing, Batch, Mobile, Address
    const rows = [
      ["Father's Name", s.fatherName],
      ["Mother's Name", s.motherName],
      ['Date of Birth', fmtDate(s.dateOfBirth)],
      ['Gender', s.gender],
      ['Blood Group', s.bloodGroup],
      ['Category', s.category],
      ['Admission Date', fmtDate(s.date_of_admission)],
      ['Valid Till', vt],
    ];
    return `
    <div class="card front">
      <div class="left-strip">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style="position:absolute;inset:0;pointer-events:none">
          <defs>
            <pattern id="${patternId}" patternUnits="userSpaceOnUse" width="80" height="80">
              <path d="M15 15 L10 20 L15 25" stroke="rgba(251,178,31,0.15)" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M25 15 L30 20 L25 25" stroke="rgba(251,178,31,0.15)" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M58 50 Q55 50 55 53 L55 57 Q55 60 52 60 Q55 60 55 63 L55 67 Q55 70 58 70" stroke="rgba(255,255,255,0.12)" stroke-width="1.5" fill="none" stroke-linecap="round"/>
              <path d="M62 50 Q65 50 65 53 L65 57 Q65 60 68 60 Q65 60 65 63 L65 67 Q65 70 62 70" stroke="rgba(255,255,255,0.12)" stroke-width="1.5" fill="none" stroke-linecap="round"/>
              <circle cx="65" cy="25" r="1.5" fill="rgba(251,178,31,0.15)"/>
              <circle cx="20" cy="40" r="1.5" fill="rgba(255,255,255,0.1)"/>
              <rect x="50" y="15" width="3" height="3" fill="rgba(255,255,255,0.15)" rx="0.5"/>
              <rect x="70" y="40" width="3" height="3" fill="rgba(251,178,31,0.2)" rx="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#${patternId})"/>
        </svg>
        <div class="accent-bar"></div>
        <div class="school-logo"><i class="pi pi-building"></i></div>
        <div class="strip-name">${schoolName}</div>
        <div class="strip-addr">${schoolAddress}</div>
        <div class="strip-divider"></div>
        <div class="avatar-wrap">${s.profilePicture?.cdn_url
          ? `<img src="${s.profilePicture.cdn_url}" class="avatar-img"/>`
          : `<div class="avatar-init">${getInitials(s.name)}</div>`}</div>
        <div class="strip-student-name">${s.name}</div>
        <div class="student-badge"><i class="pi pi-graduation-cap"></i> Student</div>
        <div class="bottom-bar"></div>
      </div>
      <div class="right-area">
        <div class="title-row">
          <div><div class="id-label">Student Identity Card</div><div class="id-name">${s.name}</div></div>
          <div class="enroll-badge">${s.enrollment_id || s.student_id.slice(-6).toUpperCase()}</div>
        </div>
        <div class="info-grid">
          ${rows.map(([label, val]) => `
            <div class="info-cell">
              <div class="info-label">${label}</div>
              <div class="info-value">${val || '-'}</div>
            </div>`).join('')}
        </div>
        <div class="bottom-strip-right"></div>
      </div>
    </div>`;
  };

  const backCard = (s: Student) => {
    const emergencyName = s.emergencyContactName || s.fatherName || s.motherName || '-';
    const emergencyPhone = s.emergencyContactPhone || s.mobileNumber || '-';
    const addr = [s.villageTown, s.district, s.state].filter(Boolean).join(', ') || '-';
    const patternId = `pattern-back-${s.student_id}`;
    
    return `
    <div class="card back">
      <div class="back-header">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style="position:absolute;inset:0;pointer-events:none">
          <defs>
            <pattern id="${patternId}" patternUnits="userSpaceOnUse" width="80" height="80">
              <path d="M15 15 L10 20 L15 25" stroke="rgba(251,178,31,0.15)" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M25 15 L30 20 L25 25" stroke="rgba(251,178,31,0.15)" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M58 50 Q55 50 55 53 L55 57 Q55 60 52 60 Q55 60 55 63 L55 67 Q55 70 58 70" stroke="rgba(255,255,255,0.12)" stroke-width="1.5" fill="none" stroke-linecap="round"/>
              <path d="M62 50 Q65 50 65 53 L65 57 Q65 60 68 60 Q65 60 65 63 L65 67 Q65 70 62 70" stroke="rgba(255,255,255,0.12)" stroke-width="1.5" fill="none" stroke-linecap="round"/>
              <circle cx="65" cy="25" r="1.5" fill="rgba(251,178,31,0.15)"/>
              <circle cx="20" cy="40" r="1.5" fill="rgba(255,255,255,0.1)"/>
              <rect x="50" y="15" width="3" height="3" fill="rgba(255,255,255,0.15)" rx="0.5"/>
              <rect x="70" y="40" width="3" height="3" fill="rgba(251,178,31,0.2)" rx="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#${patternId})"/>
        </svg>
        <div class="accent-bar"></div>
        <div class="bh-left">
          <div class="school-logo-sm"><i class="pi pi-building"></i></div>
          <div><div class="bh-name">${schoolName}</div><div class="bh-addr">${schoolAddress}</div></div>
        </div>
        <div class="bh-right"><div class="bh-id-label">Student ID</div><div class="bh-id">${s.enrollment_id || s.student_id.slice(-6).toUpperCase()}</div></div>
      </div>
      <div class="back-body">
        <div class="back-left">
          <div class="section-title"><i class="pi pi-phone gold-icon"></i> Contact Details</div>
          <div class="contact-row"><div class="icon-box"><i class="pi pi-phone"></i></div><div><div class="info-label">Mobile</div><div class="info-value">${s.mobileNumber || '-'}</div></div></div>
          <div class="contact-row"><div class="icon-box"><i class="pi pi-map-marker"></i></div><div><div class="info-label">Address</div><div class="info-value">${addr}</div></div></div>
          <div class="emergency-box">
            <div class="section-title" style="margin-bottom:6px"><i class="pi pi-exclamation-triangle gold-icon"></i> Emergency Contact</div>
            <div style="display:flex;justify-content:space-between">
              <div><div class="info-label">Name</div><div class="info-value">${emergencyName}</div></div>
              <div style="text-align:right"><div class="info-label">Phone</div><div class="info-value">${emergencyPhone}</div></div>
            </div>
            ${s.bloodGroup ? `<div class="blood-row"><i class="pi pi-heart-fill"></i> Blood: <strong>${s.bloodGroup}</strong></div>` : ''}
          </div>
          <div class="notes-box">
            <div class="section-title" style="margin-bottom:4px">Note</div>
            <div class="note-item">This card is property of the institute.</div>
            <div class="note-item">Must be carried within institute premises.</div>
            <div class="note-item">Non-transferable, valid for enrolled course only.</div>
          </div>
        </div>
        <div class="back-right">
          <div class="qr-label">Scan to Verify</div>
          <div class="qr-box" id="qr-${s.student_id}"></div>
          <div class="qr-url">${websiteUrl}</div>
          <div class="sig-area">
            <div class="sig-script">ℰ𝓊𝓌</div>
            <div class="sig-line"></div>
            <div class="sig-label">Issuing Authority</div>
          </div>
        </div>
      </div>
      <div class="bottom-strip-full"></div>
    </div>`;
  };

  const css = `
    *{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact;color-adjust:exact}
    body{font-family:'Plus Jakarta Sans',sans-serif;background:#f0f4fa;padding:20px}

    /* Each student row = front (left) + back (right) side by side */
    .pair{
      display:flex;
      flex-direction:row;
      gap:20px;
      margin-bottom:40px;
      page-break-inside:avoid;
      justify-content:center;
    }

    .card{width:560px;height:320px;background:#fff !important;border-radius:20px;overflow:hidden;box-shadow:0 8px 30px rgba(14,34,93,0.15);position:relative;display:flex;flex-direction:column;flex-shrink:0}
    .accent-bar{position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,#0e225d,#fbb21f) !important;z-index:5}
    .bottom-bar,.bottom-strip-right,.bottom-strip-full{height:3px;background:linear-gradient(90deg,#0e225d,#fbb21f,#0e225d) !important}

    .front{flex-direction:row}
    .left-strip{width:160px;height:100%;background:linear-gradient(180deg,#0e225d 0%,#1a3a7a 60%,#234495 100%) !important;display:flex;flex-direction:column;align-items:center;padding:20px 10px;position:relative;flex-shrink:0}
    .school-logo{width:40px;height:40px;border-radius:50%;background:#fff !important;display:flex;align-items:center;justify-content:center;margin-bottom:6px;box-shadow:0 2px 8px rgba(0,0,0,0.2);position:relative;z-index:5}
    .school-logo .pi{font-size:18px;color:#0e225d}
    .strip-name{font-size:9px;font-weight:800;color:#fff;text-align:center;line-height:1.3;position:relative;z-index:5}
    .strip-addr{font-size:7px;color:rgba(255,255,255,0.65);text-align:center;margin-top:3px;line-height:1.4;position:relative;z-index:5}
    .strip-divider{width:80%;height:1px;background:rgba(255,255,255,0.15);margin:10px 0;position:relative;z-index:5}
    .avatar-wrap{width:78px;height:78px;border-radius:39px;border:3px solid rgba(255,255,255,0.3);overflow:hidden;background:#fbb21f !important;display:flex;align-items:center;justify-content:center;position:relative;z-index:5}
    .avatar-img{width:100%;height:100%;object-fit:cover}
    .avatar-init{font-size:24px;font-weight:800;color:#0e225d}
    .strip-student-name{font-size:11px;font-weight:800;color:#fff;text-align:center;margin-top:8px;line-height:1.3;position:relative;z-index:5}
    .student-badge{display:inline-flex;align-items:center;gap:4px;background:rgba(251,178,31,0.2) !important;border:1px solid rgba(251,178,31,0.4);border-radius:6px;padding:3px 8px;font-size:7.5px;font-weight:700;color:#fbb21f;text-transform:uppercase;margin-top:5px;position:relative;z-index:5}
    .student-badge .pi{font-size:8px}
    .right-area{flex:1;display:flex;flex-direction:column;padding:14px 18px}
    .title-row{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px}
    .id-label{font-size:8px;font-weight:700;color:#fbb21f;text-transform:uppercase;letter-spacing:0.8px}
    .id-name{font-size:15px;font-weight:800;color:#0e225d;letter-spacing:-0.3px;margin-top:2px}
    .enroll-badge{padding:4px 10px;background:rgba(14,34,93,0.06) !important;border:1px solid rgba(14,34,93,0.12);border-radius:8px;font-size:10px;font-weight:700;color:#0e225d;flex-shrink:0}
    .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:5px 14px;flex:1}
    .info-cell{display:flex;flex-direction:column}
    .info-label{font-size:7.5px;font-weight:600;color:#9ca3af;text-transform:uppercase;letter-spacing:0.3px}
    .info-value{font-size:10px;font-weight:700;color:#0e225d;margin-top:1px}
    .bottom-strip-right{height:3px;background:linear-gradient(90deg,#0e225d,#fbb21f,#0e225d) !important;border-radius:2px;margin-top:8px}

    .back{flex-direction:column}
    .back-header{background:linear-gradient(135deg,#0e225d 0%,#1a3a7a 60%,#234495 100%) !important;padding:12px 18px;position:relative;display:flex;justify-content:space-between;align-items:center;flex-shrink:0}
    .bh-left{display:flex;align-items:center;gap:8px;position:relative;z-index:5}
    .school-logo-sm{width:32px;height:32px;border-radius:50%;background:#fff !important;display:flex;align-items:center;justify-content:center}
    .school-logo-sm .pi{font-size:14px;color:#0e225d}
    .bh-name{font-size:12px;font-weight:800;color:#fff}
    .bh-addr{font-size:8px;color:rgba(255,255,255,0.7);margin-top:1px}
    .bh-right{text-align:right;position:relative;z-index:5}
    .bh-id-label{font-size:7.5px;color:rgba(255,255,255,0.6);font-weight:600;text-transform:uppercase;letter-spacing:0.5px}
    .bh-id{font-size:12px;font-weight:800;color:#fbb21f;margin-top:1px}
    .back-body{flex:1;display:flex;overflow:hidden}
    .back-left{flex:1;padding:10px 14px;display:flex;flex-direction:column;gap:7px;border-right:1px solid #E8EEF7;overflow:hidden}
    .section-title{font-size:8px;font-weight:700;color:#0e225d;text-transform:uppercase;letter-spacing:0.5px;display:flex;align-items:center;gap:4px}
    .gold-icon{color:#fbb21f !important}
    .contact-row{display:flex;align-items:center;gap:7px;margin-bottom:4px}
    .icon-box{width:22px;height:22px;border-radius:6px;background:#E8EEF7 !important;display:flex;align-items:center;justify-content:center;flex-shrink:0}
    .icon-box .pi{font-size:9px;color:#0e225d}
    .emergency-box{background:rgba(14,34,93,0.04) !important;border-radius:8px;padding:7px 8px;border:1px solid rgba(14,34,93,0.10)}
    .blood-row{display:flex;align-items:center;gap:4px;margin-top:5px;font-size:8px;color:#dc2626;font-weight:600}
    .blood-row .pi{font-size:8px;color:#dc2626}
    .notes-box{background:rgba(251,178,31,0.06) !important;border-radius:7px;padding:6px 8px;border:1px solid rgba(251,178,31,0.2)}
    .note-item{font-size:7.5px;color:#6b7280;line-height:1.5;padding-left:8px;position:relative}
    .note-item::before{content:'•';position:absolute;left:0;color:#fbb21f}
    .back-right{width:130px;display:flex;flex-direction:column;align-items:center;justify-content:space-between;padding:12px 10px;background:rgba(14,34,93,0.02) !important}
    .qr-label{font-size:7.5px;font-weight:700;color:#0e225d;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;text-align:center}
    .qr-box{width:76px;height:76px;border:2px solid #E8EEF7;border-radius:8px;overflow:hidden;background:#fff !important;display:flex;align-items:center;justify-content:center}
    .qr-url{font-size:7.5px;font-weight:700;color:#0e225d;margin-top:5px;letter-spacing:0.4px;text-align:center}
    .sig-area{text-align:center;width:100%;border-top:1px solid #E8EEF7;padding-top:8px}
    .sig-script{font-size:26px;color:#1a3a7a;font-style:italic;letter-spacing:-1px;font-weight:700;line-height:1;margin-bottom:3px}
    .sig-line{width:70%;height:1px;background:#0e225d;margin:3px auto}
    .sig-label{font-size:7.5px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.4px}

    @media print{
      body{background:white !important;padding:0;margin:0}
      .pair{margin-bottom:20px;gap:12px}
      .card{box-shadow:none}
      @page{size:A3 landscape;margin:8mm}
    }
  `;

  // Side-by-side: front on left, back on right
  const pairs = cards.map(s => `
    <div class="pair">
      ${frontCard(s)}
      ${backCard(s)}
    </div>
  `).join('');

  return `<!DOCTYPE html><html><head><title>Student ID Cards</title>
    <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet">
    <link href="https://unpkg.com/primeicons@7.0.0/primeicons.css" rel="stylesheet">
    <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"><\/script>
    <style>${css}</style>
    </head><body>
    <div style="display:flex;flex-direction:column;align-items:center;">
      ${pairs}
    </div>
    <script>
      window.onload = function() {
        document.querySelectorAll('[id^="qr-"]').forEach(function(el) {
          new QRCode(el, {text:"https://${websiteUrl}",width:76,height:76,colorDark:"#0e225d",colorLight:"#ffffff",correctLevel:QRCode.CorrectLevel.M});
        });
        setTimeout(function(){ window.print(); }, 900);
      };
    <\/script></body></html>`;
};

// ─── Main Component ────────────────────────────────────────────────────────────

const IdCardGenerator: React.FC = () => {
  const { profile, fetchProfile } = useOperatorProfileStore();

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [batchInfo, setBatchInfo] = useState<BatchInfo | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewStudent, setPreviewStudent] = useState<Student | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [schoolName, setSchoolName] = useState('School Name');
  const [schoolAddress, setSchoolAddress] = useState('Place your address, District State – 000000');
  const [websiteUrl, setWebsiteUrl] = useState('hciindia.in');
  const [validTill, setValidTill] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    if (profile) { setSchoolName(profile.institute_name); setSchoolAddress(profile.branch_name); }
  }, [profile]);

  useEffect(() => {
    if (batchInfo?.valid_till) setValidTill(batchInfo.valid_till);
  }, [batchInfo]);

  const batchOptions: DropdownOption[] = batches.map(b => ({ label: `${b.batch_name} · ${b.batch_time}`, value: b.batch_id }));

  useEffect(() => {
    (async () => {
      try {
        setBatchesLoading(true);
        const res = await apiClient.get('/v1/batches');
        if (res.data.success) {
          const active = res.data.data.filter((b: Batch) => b.is_active);
          setBatches(active);
          if (active.length > 0) setSelectedBatchId(active[0].batch_id);
        }
      } catch { setError('Failed to load batches'); }
      finally { setBatchesLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!selectedBatchId) return;
    (async () => {
      try {
        setStudentsLoading(true); setError(null); setSelectedIds(new Set());
        const res = await apiClient.get(`/v1/students/get-students-by-batch/${selectedBatchId}?status=active&limit=100`);
        if (res.data.success) { setStudents(res.data.data); setBatchInfo(res.data.batch_info); }
      } catch (e: any) {
        setError(e.response?.data?.error || 'Failed to load students');
        setStudents([]);
      } finally { setStudentsLoading(false); }
    })();
  }, [selectedBatchId]);

  const filtered = students.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) || (s.mobileNumber || '').includes(search)
  );

  const toggleSelect = (id: string) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => selectedIds.size === filtered.length ? setSelectedIds(new Set()) : setSelectedIds(new Set(filtered.map(s => s.student_id)));

  const handlePrint = (override?: Student[]) => {
    const pw = window.open('', '_blank');
    if (!pw) return;
    const cards = override ?? (selectedIds.size > 0 ? filtered.filter(s => selectedIds.has(s.student_id)) : filtered);
    pw.document.write(buildPrintHTML(cards, batchInfo, schoolName, schoolAddress, websiteUrl, validTill));
    pw.document.close();
  };

  const cardStyle: React.CSSProperties = { background: 'white', borderRadius: 16, boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB' };
  const colStyle: React.CSSProperties = { color: '#9ca3af', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px' };

  if (batchesLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, background: 'white', borderRadius: 16, border: '1px solid #E5E7EB' }}>
      <div style={{ textAlign: 'center' }}>
        <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ marginTop: 16, color: navy, fontSize: '0.95rem', fontWeight: 500 }}>Loading...</p>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .idc-row:hover { background: rgba(14,34,93,0.03) !important; }
        .idc-row { transition: background 0.15s ease; }
        .idc-cb { accent-color: #0e225d; width: 16px; height: 16px; cursor: pointer; }
        .idc-input::placeholder { color: #c4cdd8; }
        .idc-input:focus { outline: none; border-color: #0e225d !important; box-shadow: 0 0 0 3px rgba(14,34,93,0.08); }
      `}</style>

      <div style={{ padding: 0, margin: '0 auto' }}>
        {/* Header Banner */}
        <div style={{
          background: 'linear-gradient(180deg, #0e225d 0%, #1a3370 50%, #0a1a3d 100%)',
          borderRadius: 20, padding: 32, marginBottom: 24,
          color: 'white', position: 'relative', overflow: 'hidden',
          boxShadow: '2px 0 12px rgba(14,34,93,0.3)',
        }}>
          <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, background: 'rgba(251,178,31,0.08)', borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', background: 'rgba(251,178,31,0.18)', border: '1px solid rgba(251,178,31,0.35)', borderRadius: 20, marginBottom: 12, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: gold }}>
              <i className="pi pi-id-card" style={{ fontSize: '0.7rem' }}></i> ID Card Generator
            </div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '1.75rem', fontWeight: 700, color: 'white', letterSpacing: '-0.5px' }}>Student ID Cards</h1>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
              {batchInfo ? `${batchInfo.batch_name}` : 'Select a batch to generate ID cards'}
            </p>
          </div>
        </div>

        {error && <Message severity="error" text={error} style={{ marginBottom: 20, width: '100%' }} />}

        {/* Controls Row */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ ...cardStyle, padding: '20px 24px', flex: 2, minWidth: 260 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 4, height: 18, background: `linear-gradient(180deg, ${gold}, #e09a00)`, borderRadius: 4 }} />
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: navy }}><i className="pi pi-th-large" style={{ fontSize: '0.8rem', marginRight: 6 }}></i>Select Batch</span>
            </div>
            <GlobalDropdownField value={selectedBatchId} onChange={setSelectedBatchId} options={batchOptions} placeholder="Select a batch" icon="pi pi-users" />
          </div>

          <div style={{ ...cardStyle, padding: '20px 24px', flex: 3, minWidth: 280 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 4, height: 18, background: `linear-gradient(180deg, ${gold}, #e09a00)`, borderRadius: 4 }} />
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: navy }}><i className="pi pi-cog" style={{ fontSize: '0.8rem', marginRight: 6 }}></i>Card Settings</span>
              </div>
              <button onClick={() => setShowSettings(v => !v)} style={{ padding: '5px 12px', background: showSettings ? 'rgba(14,34,93,0.08)' : 'transparent', border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, color: navy }}>
                {showSettings ? 'Hide' : 'Edit'} Settings
              </button>
            </div>
            {showSettings ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { label: 'School Name', value: schoolName, set: setSchoolName, icon: 'pi-building' },
                  { label: 'Address', value: schoolAddress, set: setSchoolAddress, icon: 'pi-map-marker' },
                  { label: 'Website URL', value: websiteUrl, set: setWebsiteUrl, icon: 'pi-globe' },
                  { label: 'Valid Till (e.g. 2026-07-31)', value: validTill, set: setValidTill, icon: 'pi-clock' },
                ].map(f => (
                  <div key={f.label}>
                    <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                      <i className={`pi ${f.icon}`} style={{ fontSize: '0.72rem' }}></i> {f.label}
                    </label>
                    <input value={f.value} onChange={e => f.set(e.target.value)} className="idc-input" style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: '0.83rem', color: navy, fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.2s' }} />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {[['Institute', schoolName], ['Branch', schoolAddress], ['URL', websiteUrl], ['Valid Till', formatDate(validTill || null)]].map(([l, v]) => (
                  <div key={l} style={{ padding: '6px 12px', background: '#E8EEF7', borderRadius: 8 }}>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>{l}</div>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: navy, marginTop: 2 }}>{v}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Students Table */}
        {selectedBatchId && (
          <div style={{ ...cardStyle, marginBottom: 20, overflow: 'hidden' }}>
            <div style={{ padding: '16px 24px', borderBottom: '2px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 4, height: 22, background: `linear-gradient(180deg, ${gold}, #e09a00)`, borderRadius: 4 }} />
                <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: navy }}>
                  Students {students.length > 0 && <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#9ca3af' }}>({students.length})</span>}
                </h2>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative' }}>
                  <i className="pi pi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: '#9ca3af' }}></i>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search students..." className="idc-input" style={{ paddingLeft: 32, paddingRight: 12, height: 36, border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: '0.82rem', color: navy, fontFamily: 'inherit', width: 200, transition: 'all 0.2s' }} />
                </div>
                <button onClick={() => handlePrint()} disabled={studentsLoading || filtered.length === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 18px', height: 36, background: (studentsLoading || filtered.length === 0) ? 'rgba(14,34,93,0.3)' : `linear-gradient(135deg, ${navy} 0%, #1a3370 100%)`, border: 'none', borderRadius: 10, color: 'white', fontSize: '0.8rem', fontWeight: 700, cursor: (studentsLoading || filtered.length === 0) ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px rgba(14,34,93,0.25)' }}>
                  <i className="pi pi-print" style={{ fontSize: '0.8rem' }}></i>
                  {selectedIds.size > 0 ? `Print ${selectedIds.size} Selected` : 'Print All'}
                </button>
              </div>
            </div>

            {studentsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><ProgressSpinner style={{ width: 40, height: 40 }} strokeWidth="3" animationDuration=".8s" /></div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, background: 'rgba(14,34,93,0.06)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <i className="pi pi-users" style={{ fontSize: '1.6rem', color: navy, opacity: 0.3 }}></i>
                </div>
                <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>No students found</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...colStyle, padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #E5E7EB', background: '#fafafa' }}>
                      <input type="checkbox" className="idc-cb" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleAll} />
                    </th>
                    {['Student', 'Father', 'DOB', 'Contact', 'Blood Group', 'Admission', 'Preview'].map(h => (
                      <th key={h} style={{ ...colStyle, padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #E5E7EB', background: '#fafafa' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(student => (
                    <tr key={student.student_id} className="idc-row">
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                        <input type="checkbox" className="idc-cb" checked={selectedIds.has(student.student_id)} onChange={() => toggleSelect(student.student_id)} />
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ position: 'relative' }}>
                            <Avatar image={student.profilePicture?.cdn_url || undefined} icon={!student.profilePicture ? 'pi pi-user' : undefined} size="normal" shape="circle" style={{ width: 36, height: 36, background: student.profilePicture ? 'transparent' : '#E8EEF7', color: navy, border: '2px solid #E5E7EB' }} />
                            {student.is_registered && <div style={{ position: 'absolute', bottom: -1, right: -1, width: 12, height: 12, background: '#16a34a', borderRadius: '50%', border: '2px solid white' }} />}
                          </div>
                          <div>
                            <div style={{ fontSize: '0.83rem', fontWeight: 600, color: navy }}>{student.name}</div>
                            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{student.enrollment_id || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', color: '#6b7280' }}>{student.fatherName || '—'}</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', color: '#6b7280' }}>{formatDOB(student.dateOfBirth)}</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', color: '#6b7280' }}>{student.mobileNumber || '—'}</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                        {student.bloodGroup ? (
                          <span style={{ display: 'inline-block', padding: '2px 10px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, color: '#dc2626' }}>{student.bloodGroup}</span>
                        ) : <span style={{ color: '#9ca3af', fontSize: '0.82rem' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem', color: '#6b7280' }}>{student.date_of_admission || '—'}</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                        <button onClick={() => setPreviewStudent(student)} title="Preview ID Card" style={{ width: 32, height: 32, background: 'transparent', border: '1px solid transparent', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', transition: 'all 0.2s' }} onMouseEnter={e => { e.currentTarget.style.background = 'rgba(14,34,93,0.08)'; e.currentTarget.style.color = navy; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af'; }}>
                          <i className="pi pi-id-card" style={{ fontSize: '0.85rem' }}></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!studentsLoading && filtered.length > 0 && (
              <div style={{ padding: '12px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>
                  {selectedIds.size > 0
                    ? <><strong style={{ color: navy }}>{selectedIds.size}</strong> selected of {filtered.length}</>
                    : <>{filtered.length} students — <span style={{ color: gold, fontWeight: 600 }}>select rows to print specific cards</span></>}
                </span>
                {selectedIds.size > 0 && <button onClick={() => setSelectedIds(new Set())} style={{ fontSize: '0.75rem', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Clear selection</button>}
              </div>
            )}
          </div>
        )}

        {!selectedBatchId && batches.length > 0 && (
          <div style={{ ...cardStyle, padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, background: 'rgba(14,34,93,0.06)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <i className="pi pi-id-card" style={{ fontSize: '1.8rem', color: navy, opacity: 0.3 }}></i>
            </div>
            <h3 style={{ margin: '0 0 8px 0', color: navy, fontSize: '1.2rem', fontWeight: 700 }}>Select a Batch</h3>
            <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>Choose a batch above to load students and generate ID cards</p>
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      {previewStudent && (
        <Dialog visible={!!previewStudent} onHide={() => setPreviewStudent(null)} style={{ width: 'auto' }} dismissableMask header={false}
          pt={{
            root: { style: { borderRadius: 20, overflow: 'hidden', border: '1px solid #E5E7EB', boxShadow: '0 20px 60px rgba(14,34,93,0.2)', background: 'transparent' } },
            header: { style: { display: 'none' } },
            content: { style: { padding: 24, background: '#E8EEF7', borderRadius: 20 } },
          }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: navy }}><i className="pi pi-id-card" style={{ marginRight: 6 }}></i>ID Card Preview</span>
              <button onClick={() => setPreviewStudent(null)} style={{ background: 'rgba(14,34,93,0.08)', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: navy }}>
                <i className="pi pi-times" style={{ fontSize: '0.8rem' }}></i>
              </button>
            </div>

            {/* Preview: front and back stacked (for dialog readability) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Front Side</div>
              <IdCardFront student={previewStudent} batchInfo={batchInfo} schoolName={schoolName} schoolAddress={schoolAddress} websiteUrl={websiteUrl} validTill={validTill || batchInfo?.valid_till} />
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Back Side</div>
              <IdCardBack student={previewStudent} batchInfo={batchInfo} schoolName={schoolName} schoolAddress={schoolAddress} websiteUrl={websiteUrl} validTill={validTill || batchInfo?.valid_till} />
            </div>

            <button onClick={() => { const s = previewStudent; setPreviewStudent(null); handlePrint([s]); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 24px', background: `linear-gradient(135deg, ${navy} 0%, #1a3370 100%)`, border: 'none', borderRadius: 10, color: 'white', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(14,34,93,0.3)' }}>
              <i className="pi pi-print" style={{ fontSize: '0.85rem' }}></i> Print This Card
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default IdCardGenerator;